<template>
  <div id="bulk-download" class="s-base">
    <v-card class="s-panel" max-width="1000">
      <v-card-title class="s-title">一括配信ダウンロード画面</v-card-title>
      <v-container>
        <v-row class="selectMenu px-3">
          <v-col>
            <div>契約データ種別</div>
            <v-select
              v-model="dataType"
              :items="dataTypeList"
              item-text="name"
              item-value="code"
              class="pa-0 ma-0"
            ></v-select>
          </v-col>
          <v-col>
            <div>契約エリア</div>
            <v-select
              v-if="dataType === DATA_TYPE_CONST.FRL.code"
              v-model="rsys"
              :disabled="!dataType"
              :items="rsysList"
              item-text="rsysNm"
              item-value="rsysCd"
              class="pa-0 ma-0"
            ></v-select>
            <v-select
              v-else
              v-model="pref"
              :disabled="!dataType"
              :items="prefList"
              item-text="prefNm"
              item-value="prefCd"
              class="pa-0 ma-0"
            ></v-select>
          </v-col>
          <v-col>
            <div>日付</div>
            <v-select
              v-if="dataType === DATA_TYPE_CONST.FRL.code"
              v-model="date"
              :disabled="!rsys"
              :items="rsysDateList"
              item-text="dateStr"
              class="pa-0 ma-0"
            ></v-select>
            <v-select
              v-else
              v-model="date"
              :disabled="!pref"
              :items="prefDateList"
              item-text="dateStr"
              class="pa-0 ma-0"
            ></v-select>
          </v-col>
        </v-row>
        <v-row class="d-flex flex-row px-3">
          <v-btn class="menu-btn" color="primary" x-large @click="clickReturn()"
            >戻る</v-btn
          >
          <v-btn
            class="menu-btn ml-auto"
            color="secondary"
            x-large
            @click="download()"
            >ダウンロード</v-btn
          >
        </v-row>
      </v-container>
    </v-card>
    <request-error-dialog
      :requestErrorDialogFlag.sync="dialog.requestError"
      :httpStatus="httpStatus"
      :contractsInfoExists="contractsInfoExists"
      message=""
    />
    <v-dialog v-model="downloading" persistent max-width="600">
      <v-card>
        <v-card-text>
          <v-row align="center" justify="center">
            <v-col class="text-center black--text">
              一括配信データをダウンロード中です。しばらくお待ち下さい。
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-progress-linear indeterminate />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import { saveAs } from "file-saver";
import loginModule from "../store/LoginModule";
import { Component, Vue } from "vue-property-decorator";
import { userApi, Contract, Pref, Rsys, DateObj, ContractResponse } from "../api/UserApi";
import { NETWORK_ERROR } from "../router";
import RequestErrorDialog from "./RequestErrorDialog.vue";
import Axios from "axios";

interface DataType {
  name: string;
  code: number;
}

@Component({
  components: {
    "request-error-dialog": RequestErrorDialog,
  },
})
export default class BulkDownload extends Vue {
  // データ種別
  readonly DATA_TYPE_CONST = {
    CM: {
      name: "危機管理",
      code: 1
    },
    SCAM: {
      name: "簡易カメラ",
      code: 2
    },
    CCTV: {
      name: "CCTV",
      code: 3,
    },
    FRL: {
      name: "水害リスクライン",
      code: 4
    }
  };

  // 配信区分：一括配信
  readonly BULK_CONTRACT_CODE = 3;

  dialog = {
    requestError: false,
  };

  httpStatus = 500;
  contractsInfoExists = true;

  downloading = false;

  dataType = 0;
  pref = 0;
  rsys = "";
  date = "";
  contracts: Contract[] = [];

  clickReturn() {
    this.$router.push({ name: "Menu" });
  }

  public get dataTypeList(): DataType[] {
    const bulkDataTypeList = this.contracts
      .filter(item => item.delivCd === this.BULK_CONTRACT_CODE)
      .map(item => item.dataType);

    const dataTypeList = Object.values(this.DATA_TYPE_CONST).filter(item =>
      bulkDataTypeList.includes(item.code)
    );

    if (dataTypeList) {
      return dataTypeList;
    } else {
      return [];
    }
  }

  public get prefList(): Pref[] {
    const prefList = this.contracts.find(
      (contract) => contract.dataType === this.dataType
    )?.prefList;
    if (prefList) {
      return prefList;
    } else {
      return [];
    }
  }

  public get rsysList(): Rsys[] {
    const rsysList = this.contracts.find(
      (contract) => contract.dataType === this.dataType
    )?.rsysList;
    if (rsysList) {
      return rsysList;
    } else {
      return [];
    }
  }

  public get prefDateList(): DateObj[] {
    const pref = this.prefList.find((pref) => pref.prefCd === this.pref);
    if (pref?.dateList) {
      return pref.dateList;
    } else {
      return [];
    }
  }

  public get rsysDateList(): DateObj[] {
    const rsys = this.rsysList.find((rsys) => rsys.rsysCd === this.rsys);
    if (rsys?.dateList) {
      return rsys.dateList;
    } else {
      return [];
    }
  }

  download() {
    const targetDateList = this.dataType === this.DATA_TYPE_CONST.FRL.code
      ? this.rsysDateList
      : this.prefDateList;
    const fileList = targetDateList.find(
      (date) => date.dateStr === this.date
    )?.fileList;
    if (fileList) {
      for (const file of fileList) {
        this.fileDownload(file);
      }
    }
  }

  /**
   * 一括配信用ファイル一覧情報を取得
   */
  async loadAllData(): Promise<void> {
    let result: { httpStatus: number; contractResponse: ContractResponse };
    try {
      result = await userApi.getBulkContract(loginModule.userId, true);
      this.contracts = result.contractResponse.result;
    } catch (error) {
      this.contracts = [];
      let newError: Error;
      if (Axios.isAxiosError(error) && error.response) {
        newError = new Error(error.response.status.toString());
      } else {
        newError = new Error(NETWORK_ERROR);
      }
      if (newError.message === "401") {
        // 401 Unauthorized
        this.httpStatus = 401;
        this.dialog.requestError = true;
      } else if (newError.message === "404") {
        // 404 Not Found
        this.httpStatus = 404;
        this.contractsInfoExists = false;
        this.dialog.requestError = true;
      } else if (newError.message === "500") {
        // 500 Internal Server Error
        this.httpStatus = 500;
        this.dialog.requestError = true;
      } else {
        this.httpStatus = parseInt(newError.message);
        // 運用上起こってはいけないエラー
        await this.$router.push({ name: newError.message });
      }
    }
  }

  /**
   * ファイルをダウンロード
   */
  async fileDownload(filePath: string) {
    let result: { httpStatus: number; headers: any; responseData: Blob };
    try {
      this.downloading = true;
      result = await userApi.getDownloadData(filePath);

      if (result.responseData && result.httpStatus === 200) {
        const contentDisposition = result.headers["content-disposition"];
        const contentDispositions = contentDisposition.split(";");
        const fileNames =
          contentDispositions[contentDispositions.length - 1].split("=");
        const fileName = fileNames[fileNames.length - 1];
        // ファイル名の前後のダブルクォーテーションを削除
        // eslint-disable-next-line
        const fileNameStr = fileName.replace(/\"/g, "");
        saveAs(result.responseData, fileNameStr);
      }
      this.downloading = false;
    } catch (error) {
      this.downloading = false;
      let newError: Error;
      if (Axios.isAxiosError(error) && error.response) {
        newError = new Error(error.response.status.toString());
      } else {
        newError = new Error(NETWORK_ERROR);
      }
      if (newError.message === "401") {
        // 401 Unauthorized
        this.httpStatus = 401;
        this.dialog.requestError = true;
      } else if (newError.message === "404") {
        // 404 Not Found
        this.httpStatus = 404;
        this.dialog.requestError = true;
      } else if (newError.message === "500") {
        // 500 Internal Server Error
        this.httpStatus = 500;
        this.dialog.requestError = true;
      } else {
        this.httpStatus = parseInt(newError.message);
        // 運用上起こってはいけないエラー
        await this.$router.push({ name: newError.message });
      }
    }
  }

  async mounted() {
    await this.loadAllData();
  }
}
</script>

<style lang="scss" scoped>
@import "@/scss/common.scss";

.selectMenu {
  min-height: 300px;
}
</style>
